import * as React from 'react';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import type { PersonalityConfig, EchoDraftResponse } from '@/types/echo_creation';

/** Manages personality config form: loads AI preview, tracks modifications, saves to API. */
export const usePersonalityConfig = (
  draftId: string,
  onComplete: () => void,
  initialConfig?: PersonalityConfig
) => {
  const { addToast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(!initialConfig);

  const [config, setConfig] = React.useState<PersonalityConfig>(
    initialConfig ?? {
      communication_style: 50,
      response_length: 50,
      motivation_intensity: 50,
      formality_level: 50,
      topics_to_emphasize: [],
      motivational_approach: [],
      interaction_style: [],
      hobbies_interests: [],
      core_values: '',
      notes: '',
      banned_content_acknowledged: false,
    }
  );
  const [originalConfig, setOriginalConfig] = React.useState<PersonalityConfig | null>(
    initialConfig ?? null
  );

  // When initialConfig is preloaded (from training), it won't have the existing
  // banned_content_acknowledged flag. Fetch it from the draft's saved config.
  React.useEffect(() => {
    if (!initialConfig || initialConfig.banned_content_acknowledged) return;

    api.echoes
      .getDraftProfile(draftId)
      .then((draft) => {
        if (draft?.personality_config?.banned_content_acknowledged) {
          setConfig((prev) => ({ ...prev, banned_content_acknowledged: true }));
          setOriginalConfig((prev) =>
            prev ? { ...prev, banned_content_acknowledged: true } : prev
          );
        }
      })
      .catch(() => {
        // Non-fatal — checkbox stays unchecked
      });
  }, [draftId, initialConfig]);

  React.useEffect(() => {
    if (initialConfig) return;

    const loadPersonalityPreview = async () => {
      try {
        setIsLoading(true);
        const [preview, draft] = await Promise.all([
          api.echoes.getPersonalityPreview(draftId),
          api.echoes.getDraftProfile(draftId).catch(() => null),
        ]);

        // Preserve existing policy acknowledgment from saved config
        if (draft?.personality_config?.banned_content_acknowledged) {
          preview.banned_content_acknowledged = true;
        }

        setConfig(preview);
        setOriginalConfig(preview);

        if (preview.topics_to_emphasize && preview.topics_to_emphasize.length > 0) {
          addToast({
            title: 'Personality Generated',
            subtitle: 'AI has analyzed your profile. Review the settings below.',
            type: 'success',
          });
        }
      } catch {
        try {
          const draft = await api.echoes.getDraftProfile(draftId);
          if (draft.personality_config) {
            setConfig((prev) => ({ ...prev, ...draft.personality_config }));
            setOriginalConfig((prev) => ({ ...(prev ?? {}), ...draft.personality_config }));
          }
        } catch {
          // Fallback also failed — use defaults
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (draftId) {
      loadPersonalityPreview();
    }
  }, [draftId, addToast, initialConfig]);

  const updateField = React.useCallback(
    <K extends keyof PersonalityConfig>(key: K, value: PersonalityConfig[K]) => {
      setConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const resetField = React.useCallback(
    <K extends keyof PersonalityConfig>(key: K) => {
      if (originalConfig && originalConfig[key] !== undefined) {
        setConfig((prev) => ({ ...prev, [key]: originalConfig[key] as PersonalityConfig[K] }));
      }
    },
    [originalConfig]
  );

  const isFieldModified = React.useCallback(
    <K extends keyof PersonalityConfig>(key: K): boolean => {
      if (!originalConfig) return false;

      const current = config[key];
      const original = originalConfig[key];

      if (Array.isArray(current) && Array.isArray(original)) {
        const sortedCurrent = [...current].sort();
        const sortedOriginal = [...original].sort();
        return JSON.stringify(sortedCurrent) !== JSON.stringify(sortedOriginal);
      }

      return current !== original;
    },
    [config, originalConfig]
  );

  const hasModifications = React.useMemo(() => {
    if (!originalConfig) return false;
    const keys = Object.keys(config) as Array<keyof PersonalityConfig>;

    for (const key of keys) {
      const current = config[key];
      const original = originalConfig[key];

      if (Array.isArray(current) && Array.isArray(original)) {
        const sortedCurrent = [...current].sort();
        const sortedOriginal = [...original].sort();
        if (JSON.stringify(sortedCurrent) !== JSON.stringify(sortedOriginal)) return true;
      } else if (current !== original) {
        return true;
      }
    }
    return false;
  }, [config, originalConfig]);

  const resetAll = React.useCallback(() => {
    if (originalConfig) {
      setConfig(originalConfig);
    }
  }, [originalConfig]);

  const getPayload = React.useCallback(
    (): PersonalityConfig => ({
      ...config,
      banned_content_acknowledged: config.banned_content_acknowledged ?? false,
      topics_to_emphasize: config.topics_to_emphasize || [],
      motivational_approach: config.motivational_approach || [],
      interaction_style: config.interaction_style || [],
      hobbies_interests: config.hobbies_interests || [],
      core_values: config.core_values || '',
      notes: config.notes || '',
    }),
    [config]
  );

  const saveDraft = React.useCallback(async (): Promise<boolean> => {
    setIsSubmitting(true);
    try {
      const payload = getPayload();
      await api.echoes.updatePersonalityConfig(draftId, payload);

      addToast({
        title: 'Draft Saved',
        subtitle: 'Personality configuration updated.',
        type: 'success',
      });
      setOriginalConfig(payload);
      return true;
    } catch (error) {
      const msg = getErrorMessage(error);
      addToast({
        title: 'Save Failed',
        subtitle: msg,
        type: 'error',
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [draftId, getPayload, addToast]);

  const handleSubmit = React.useCallback(async () => {
    if (config.banned_content_acknowledged !== true) {
      addToast({
        title: 'Acknowledgment Required',
        subtitle: 'You must acknowledge the banned content policy.',
        type: 'error',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = getPayload();

      const response = await api.echoes.updatePersonalityConfig(draftId, payload);

      const responseConfig = (response as EchoDraftResponse).personality_config;
      if (responseConfig && responseConfig.banned_content_acknowledged !== true) {
        throw new Error('Server failed to save policy acknowledgment.');
      }

      addToast({
        title: 'Configuration Saved',
        subtitle: 'Your echo personality has been updated.',
        type: 'success',
      });

      onComplete();
    } catch (error) {
      const msg = getErrorMessage(error);
      addToast({
        title: 'Submit Failed',
        subtitle: msg,
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [config.banned_content_acknowledged, draftId, getPayload, addToast, onComplete]);

  return {
    config,
    updateField,
    resetField,
    resetAll,
    isFieldModified,
    hasModifications,
    handleSubmit,
    saveDraft,
    isSubmitting,
    isLoading,
  };
};
